import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useModal } from '../contexts/ModalContext';

/**
 * 만들어진 링크를 보여주고 복사하는 자리.
 *
 * 초대와 비밀번호 재설정이 같은 것을 보여준다. 둘 다 '관리자가 링크를 만들어
 * 손으로 건넨다' 는 같은 일이고, 두 곳에 따로 그려 두면 '다시 볼 수 없다' 는
 * 경고가 한쪽에만 남는 날이 온다.
 *
 * **메시지를 통째로 복사할 수 있게 한다.** 주소만 던지면 받는 사람은 이게
 * 무엇이고 왜 왔는지 모른다. 관리자가 매번 설명을 지어 쓰게 두면 사람마다
 * 다른 말이 나가고, 그중 하나는 결국 피싱처럼 읽힌다.
 *
 * @param link {{ kind?: 'invite' | 'reset', name, url, validText? }} 또는 null
 */
const InviteLinkModal = ({ link, onClose }) => {
    const { showAlert } = useModal();
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        setCopied(null);
    }, [link]);

    const reset = link?.kind === 'reset';

    const message = link
        ? reset
            ? `[바로보고] ${link.name}님, 비밀번호를 다시 정해 주세요.\n\n` +
              `아래 링크에서 새 비밀번호를 정하시면 바로 로그인됩니다.\n${link.url}\n\n` +
              `${link.validText || '24시간'} 안에 열어 주세요.`
            : `[바로보고] ${link.name}님을 초대합니다.\n\n` +
              `아래 링크에서 비밀번호를 정하시면 계정이 만들어집니다.\n${link.url}\n\n` +
              `${link.validText || '7일'} 안에 열어 주세요.`
        : '';

    const copy = async (what, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(what);
        } catch (err) {
            // 클립보드를 막아 둔 브라우저가 있다. 글이 보이니 직접 긁으면 된다.
            showAlert('알림', '복사하지 못했습니다. 직접 선택해 주세요.', 'warning');
        }
    };

    return (
        <Modal show={Boolean(link)} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold">
                    {reset ? '비밀번호 재설정 링크' : '초대 링크'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {link && (
                    <>
                        <p className="small mb-2">
                            <strong>{link.name}</strong>님에게 아래 메시지를 보내 주세요.
                            {reset
                                ? ' 받는 분이 직접 새 비밀번호를 정합니다.'
                                : ' 받는 분이 직접 비밀번호를 정하면 계정이 만들어집니다.'}
                        </p>

                        <div
                            className="bg-light rounded-3 p-3 mb-2"
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem' }}
                        >
                            {message}
                        </div>

                        <div className="d-flex gap-2">
                            <Button
                                variant={copied === 'message' ? 'success' : 'primary'}
                                className="flex-grow-1"
                                onClick={() => copy('message', message)}
                            >
                                {copied === 'message' ? '복사했습니다' : '메시지 복사'}
                            </Button>
                            {/* 이미 자기 말로 쓰고 있는 관리자도 있다. 주소만
                                가져갈 길을 막을 이유는 없다. */}
                            <Button
                                variant={copied === 'url' ? 'success' : 'white'}
                                className="border text-nowrap"
                                onClick={() => copy('url', link.url)}
                            >
                                {copied === 'url' ? '복사함' : '주소만'}
                            </Button>
                        </div>

                        {/* 다시 볼 수 없다는 것을 분명히 해 둔다. 창을 닫고 나서
                            찾으러 오면 없다 -- 대신 목록에서 다시 만들면 된다. */}
                        <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                            이 링크는 <strong>지금만</strong> 볼 수 있고{' '}
                            {link.validText || (reset ? '24시간' : '7일')} 뒤 만료됩니다. 놓치면{' '}
                            {reset
                                ? '사용자 목록에서 「비밀번호 재설정」을 다시 누르면 됩니다.'
                                : '가입 대기 목록에서 「링크」를 눌러 다시 만들 수 있습니다.'}
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default InviteLinkModal;
